<template>
  <div class="rich-txt-com">
    <div
      v-if="valueHtml"
      v-html="valueHtml"
      class="rich-text-wrap"
      ref="htmlRef"
      :class="{
        'large-line': largeLineHeight,
        'img-can-preview': imgCanPreview || imgCanPreviewSelf,
      }"
      @click="richTextClick"
    ></div>

    <online-preview-dialog
      title="预览"
      :oss-path="ossPath"
      :officeType="officeType"
      v-model:visible="onlineDataVis"
    />
  </div>

  <!-- 预览图片 -->
  <imgPreviewer
    ref="imgPreviewerRef"
    v-model:visible="imgPreviewVisible"
    :urlList="imgPreviewUrls"
    :initialIndex="imgPreviewIndex"
  />

  <!-- 预览视频 -->
  <tx-play-dialog
    :video-id="playerId"
    v-model:visible="playerVis"
    v-bind="$attrs"
    playMode="threeTcPlay"
  />
</template>

<script setup>
/**
 * 富文本显示器
 */
import { Base64 } from "js-base64";
import imgPreviewer from "@/components/OnlineWps/imgPreviewer.vue";
import TxPlayDialog from "@/components/TxPlayer/TxPlayDialog.vue";
import OnlinePreviewDialog from "@/components/OnlineWps/onlinePreviewDialogV2.vue";
import {isBase64} from "@/utils/ruoyi.js";
const imgPreviewerRef = ref();
const props = defineProps({
  text: {
    required: true,
    default: "",
  },
  useBase64: {
    default: true,
    type: Boolean,
  },
  replaceSpecial: {
    // 替换\n等特殊字符
    default: false,
    type: Boolean,
  },
  appendBefore: {
    // 在显示的前面追加的字符串
    type: String,
    default: "",
  },
  appendAfter: {
    // 在显示的后面追加的字符串
    type: String,
    default: "",
  },
  largeLineHeight: {
    // 加大版本行高，适配旧网校题目
    type: Boolean,
    default: false,
  },
  imgCanPreview: {
    // 富文本里面的图片能够支持放大预览
    type: Boolean,
    default: false,
  },
  imgCanPreviewSelf: {
    // 富文本里面的图片自己处理放大预览
    type: Boolean,
    default: false,
  },
  videoCanPreviewSelf: {
    // 富文本里面的视频自己处理预览
    type: Boolean,
    default: false,
  },
});

const htmlRef = ref();

const valueHtml = computed(() => {
  if (!props.text) {
    return "";
  }
  let res = isBase64(props.text) ? Base64.decode(props.text) : props.text;
  if (props.replaceSpecial) {
    res = res.replaceAll("\\n", "");
  }
  return props.appendBefore + res + props.appendAfter;
});

const playerVis = ref(false);
const playerId = ref("");
const officeType = ref("");
const ossPath = ref("");
const onlineDataVis = ref(false);
const imgPreviewVisible = ref(false);
const imgPreviewUrls = ref([]);
const imgPreviewIndex = ref(0);
const richTextClick = (e) => {
  let ele = e.target;
  while (ele) {
    if (ele.getAttribute("data-videoUrl")) {
      // 视频播放
      if (props.videoCanPreviewSelf) {
        emits("videoCanPreviewSelf", {
          playerId: ele.getAttribute("data-videoUrl"),
        });
      } else {
        playerId.value = ele.getAttribute("data-videoUrl");
        playerVis.value = true;
      }

      break;
    }

    if (
      (ele.tagName || "").toLowerCase() === "img" &&
      ele.hasAttribute("data-href")
    ) {
      const url = ele.getAttribute("src");
      const urls = [...htmlRef.value.querySelectorAll("img[data-href]")].map(
        (item) => item.getAttribute("src")
      );
      const index = urls.indexOf(url);

      if (props.imgCanPreviewSelf) {
        emits("imgCanPreviewSelf", {
          urls,
          index,
        });
      } else if (props.imgCanPreview) {
        imgPreviewUrls.value = urls;
        imgPreviewIndex.value = index;
        imgPreviewVisible.value = true;
      }

      break;
    }

    if (
      ele.getAttribute("data-filestorageid") &&
      ele.getAttribute("data-filetype") == 1
    ) {
      const fileName = ele.getAttribute("data-filename");
      const videoId = ele.getAttribute("data-filestorageid");
      previewFile({
        fileName,
        videoId,
      });
      break;
    }

    if (ele == e.currentTarget) {
      break;
    }

    ele = ele.parentNode;
  }
};

const previewFile = (item) => {
  const hostname = location.hostname;
  console.log(hostname);
  const fileType = getFileType(item.fileName).toLowerCase();
  const base = "learning/prod-api/system/study/center/toolkit/preview";
  let BASE_URL = import.meta.env.VITE_APP_BASE_URL;
  if (hostname.indexOf("localhost") > -1) {
    BASE_URL = import.meta.env.VITE_APP_BASE_URL;
  } else {
    BASE_URL = `https://${hostname}/`;
  }
  const path = `${BASE_URL}${base}/${item.videoId}/${item.fileName}`;
  if (["pdf", "png", "jpg"].includes(fileType)) {
    window.open(path, "_blank");
  } else {
    if (fileType == "xls" || fileType == "xlsx") {
      officeType.value = WebOfficeSDK.OfficeType.Spreadsheet;
    }
    if (fileType == "doc" || fileType == "docx") {
      officeType.value = WebOfficeSDK.OfficeType.Writer;
    }
    if (fileType == "pptx" || fileType == "ppt") {
      officeType.value = WebOfficeSDK.OfficeType.Presentation;
    }
    ossPath.value = path;
    onlineDataVis.value = true;
  }
};

function getFileType(fileUrl) {
  const type = fileUrl.substring(fileUrl.lastIndexOf(".") + 1);
  return type;
}

const emits = defineEmits(["imgCanPreviewSelf", "videoCanPreviewSelf"]);
</script>

<style lang="scss">
.rich-txt-com {
  .rich-text-wrap {
    line-height: 16px;

    &.large-line {
      line-height: 22px;
    }

    &.img-can-preview {
      [data-href] {
        cursor: pointer;
      }
    }

    // 兼容富文本样式
    h1 {
      font-size: 32px;
      font-weight: bold;
      margin: 20px 0;

      span {
        font-weight: bold;
      }
    }

    h2 {
      margin: 20px 0;
      font-size: 24px;
      font-weight: bold;

      span {
        font-weight: bold;
      }
    }

    h3 {
      margin: 20px 0;
      font-size: 18px;
      font-weight: bold;

      span {
        font-weight: bold;
      }
    }

    h4 {
      margin: 20px 0;
      font-size: 16px;
      font-weight: bold;

      span {
        font-weight: bold;
      }
    }

    h5 {
      margin: 20px 0;
      font-size: 13px;
      font-weight: bold;

      span {
        font-weight: bold;
      }
    }

    p {
      margin: 10px 0;
      font-size: 14px;
    }

    * {
      font-size: 14px;
    }

    strong {
      font-weight: bold;

      span {
        font-weight: bold;
      }
    }

    .italic {
      font-style: italic;

      span {
        font-style: italic;
      }
    }

    em {
      font-style: italic;

      span {
        font-style: italic;
      }
    }

    sup {
      position: relative;
      bottom: 4px;
    }

    sub {
      position: relative;
      top: 4px;
    }

    table {
      margin: 10px 0;
      border-collapse: collapse;

      td,
      th {
        padding: 2px;
        border: 1px solid #dddddd;
      }

      th {
        background-color: #f5f2f0;
      }
    }

    .light-append-after-txt-score {
      // 子题目，末尾追加的分数文字样式
      color: #777777;
    }
  }

  ol {
    list-style: initial;
    list-style-type: decimal;
    padding-left: 16px;
    li {
      list-style: initial;
      list-style-type: decimal;
    }
  }
  ul {
    list-style: initial;
    padding-left: 16px;
    li {
      list-style: initial;
    }
  }

  img {
    max-width: 100%;
  }

  word-break: break-word;
}

.preview-pop-rich-img {
  display: none;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;

  .preview-head {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    position: relative;
    z-index: 2;
    background-color: #ffffff;
    cursor: move;

    .grow {
      flex-grow: 1;
    }

    .close-icon {
      font-size: 24px;
      line-height: 18px;
      color: #777777;
      cursor: pointer;
      position: relative;
      top: -1px;
      margin-left: 15px;
    }
  }

  .img-wrap {
    max-height: calc(100vh - 50px);
    overflow-y: auto;
    .popup-image {
      width: max(30vw, 300px);
      height: auto;
      transform-origin: center center;
      transition: transform 0.3s ease;
    }
  }
}
</style>
